/** Radial blur — Spin or Zoom around an in-canvas center point.
 *
 *  Spin mode rotates each sample around the center by a small angle
 *  step, averages N rotations. Zoom mode scales each sample's radial
 *  distance to the center by a small factor step, averages N scales.
 *  Both effects intensify as `amount` grows.
 *
 *  Center is stored normalized (0..1 per axis) on the config so a
 *  later resize of the source still puts the blur center on the
 *  same visual feature. Resolved to pixel coords at apply time.
 *
 *  `amount` ∈ [0, 100] maps to:
 *    - sample count: 4..32 (more samples = smoother but slower)
 *    - max angle (spin): 0..30°  (the half-arc each sample spans)
 *    - max zoom range (zoom): 0..0.30 (sample at scale 1±range)
 *
 *  Identity at amount == 0 — kernel collapses to a single sample at
 *  the source coordinate, output equals source. */

export interface RadialBlurValue {
  mode: 'spin' | 'zoom';
  /** 0..100. */
  amount: number;
  /** Normalized 0..1, where (0.5, 0.5) is the canvas midpoint. */
  centerX: number;
  centerY: number;
}

export const identityRadialBlurValue = (): RadialBlurValue => ({
  mode: 'spin',
  amount: 0,
  centerX: 0.5,
  centerY: 0.5,
});

/** Map `amount` 0..100 to a sample count. Floor at 4 even for tiny
 *  amounts so the integration error doesn't show up as banding —
 *  visually we only ever skip the math when amount is exactly 0. */
function sampleCountFromAmount(amount: number): number {
  return Math.max(4, Math.round(4 + (amount / 100) * 28));
}

export function applyRadialBlurPixels(source: ImageData, value: RadialBlurValue): ImageData {
  const w = source.width;
  const h = source.height;
  const amount = Math.max(0, Math.min(100, value.amount));
  if (amount === 0) {
    return new ImageData(new Uint8ClampedArray(source.data), w, h);
  }
  const cx = value.centerX * w;
  const cy = value.centerY * h;
  const samples = sampleCountFromAmount(amount);
  const src = source.data;
  const out = new Uint8ClampedArray(src.length);

  if (value.mode === 'spin') {
    // Half-arc in radians — total swept angle is 2*maxAngle, sampled
    // symmetrically around the source angle.
    const maxAngleDeg = (amount / 100) * 30;
    const maxAngle = (maxAngleDeg * Math.PI) / 180;
    // Pre-compute per-sample sin/cos so the inner loop is just a 2x2
    // rotation. Avoids `samples * w * h` trig calls.
    const cosT = new Float32Array(samples);
    const sinT = new Float32Array(samples);
    for (let s = 0; s < samples; s++) {
      // Spread samples evenly across the arc (-maxAngle..+maxAngle).
      // `samples` is guaranteed ≥4 by `sampleCountFromAmount`, so
      // (samples - 1) is always positive — no divide-by-zero guard
      // needed.
      const t = -maxAngle + (2 * maxAngle * s) / (samples - 1);
      cosT[s] = Math.cos(t);
      sinT[s] = Math.sin(t);
    }
    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        const dx = x - cx;
        const dy = y - cy;
        let rAcc = 0;
        let gAcc = 0;
        let bAcc = 0;
        for (let s = 0; s < samples; s++) {
          const sx = Math.min(w - 1, Math.max(0, Math.round(cx + dx * cosT[s] - dy * sinT[s])));
          const sy = Math.min(h - 1, Math.max(0, Math.round(cy + dx * sinT[s] + dy * cosT[s])));
          const si = (sy * w + sx) * 4;
          rAcc += src[si];
          gAcc += src[si + 1];
          bAcc += src[si + 2];
        }
        const di = (y * w + x) * 4;
        out[di] = rAcc / samples;
        out[di + 1] = gAcc / samples;
        out[di + 2] = bAcc / samples;
        out[di + 3] = src[di + 3];
      }
    }
  } else {
    // Zoom: scale each sample's radial distance from center by a
    // small factor centered on 1. Range is symmetric, so half the
    // samples zoom out and half zoom in — same average position
    // as the source pixel, giving a streak-toward-center look.
    const maxRange = (amount / 100) * 0.3;
    const scales = new Float32Array(samples);
    for (let s = 0; s < samples; s++) {
      scales[s] = 1 - maxRange + (2 * maxRange * s) / (samples - 1);
    }
    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        const dx = x - cx;
        const dy = y - cy;
        let rAcc = 0;
        let gAcc = 0;
        let bAcc = 0;
        for (let s = 0; s < samples; s++) {
          const sx = Math.min(w - 1, Math.max(0, Math.round(cx + dx * scales[s])));
          const sy = Math.min(h - 1, Math.max(0, Math.round(cy + dy * scales[s])));
          const si = (sy * w + sx) * 4;
          rAcc += src[si];
          gAcc += src[si + 1];
          bAcc += src[si + 2];
        }
        const di = (y * w + x) * 4;
        out[di] = rAcc / samples;
        out[di + 1] = gAcc / samples;
        out[di + 2] = bAcc / samples;
        out[di + 3] = src[di + 3];
      }
    }
  }
  return new ImageData(out, w, h);
}

export function applyRadialBlurToCanvas(
  source: ImageData,
  target: HTMLCanvasElement,
  value: RadialBlurValue,
): void {
  const tCtx = target.getContext('2d');
  if (!tCtx) return;
  if (target.width !== source.width || target.height !== source.height) {
    target.width = source.width;
    target.height = source.height;
  }
  tCtx.putImageData(applyRadialBlurPixels(source, value), 0, 0);
}
